import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import styled from 'styled-components';
import Button from '../../common/Button';
import palette from '../../../lib/styles/palette';
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { FaArrowLeft } from 'react-icons/fa';
import { ImPlus } from 'react-icons/im';
import { FaTrash } from 'react-icons/fa';

const FullScreen = styled.div`
  position: fixed;
  z-index: 30;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.5);
`;

const ModalBlock = styled.div`
  background: #fff;
  border-radius: 8px;
  width: 95%;
  max-width: 1400px;
  height: 90vh;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h2 {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 600;
    color: #212529;
  }
`;

const ModalContent = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`;

const LeftSection = styled.div`
  flex: 1;
  padding: 2rem;
  overflow-y: auto;
  border-right: 1px solid #e9ecef;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${palette.webGray[4]};
    border-radius: 10px;
  }
  &::-webkit-scrollbar-track {
    background-color: ${palette.webGray[2]};
    border-radius: 10px;
  }
`;

const RightSection = styled.div`
  width: 400px;
  padding: 2rem;
  background: #f8f9fa;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  
  label {
    display: block;
    font-size: 0.95rem;
    font-weight: 600;
    color: #495057;
    margin-bottom: 0.5rem;
    
    &::before {
      content: '';
      display: inline-block;
      width: 4px;
      height: 16px;
      background: ${palette.webCyan[5]};
      margin-right: 8px;
      border-radius: 2px;
    }
  }
  
  input {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 0.9rem;
    font-family: inherit;
    
    &:focus {
      outline: none;
      border-color: ${palette.webCyan[5]};
    }
  }
`;

const QuillBox = styled.div`
  .ql-container {
    min-height: 400px;
    max-height: 400px;
    font-size: 0.95rem;
  }
  
  .ql-editor {
    min-height: 400px;
    max-height: 400px;
  }
`;

const ImageUploadBox = styled.div`
  width: 100%;
  height: 150px;
  border: 2px dashed #ddd;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  background: #f8f9fa;
  transition: all 0.2s;
  position: relative;
  
  &:hover {
    border-color: ${palette.webCyan[5]};
    background: #fff;
  }
  
  .placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    color: #999;
    
    svg {
      font-size: 2rem;
      margin-bottom: 8px;
    }
    
    span {
      font-size: 0.9rem;
    }
  }
  
  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
  
  .remove-btn {
    position: absolute;
    top: 10px;
    right: 10px;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.6);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    
    &:hover {
      background: rgba(0, 0, 0, 0.8);
    }
    
    svg {
      font-size: 0.9rem;
    }
  }
`;

const PreviewLabel = styled.div`
  font-size: 0.95rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 1rem;
`;

const PreviewBox = styled.div`
  width: 100%;
  max-width: 350px;
  border: 1px solid ${palette.webGray[5]};
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const PreviewTop = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 45px;
  background: #fff;
  border-bottom: 1px solid #e9ecef;
`;

const Top1 = styled.div`
  width: 8px;
  height: 8px;
  background-color: ${palette.webGray[5]};
  margin-right: 15px;
  border-radius: 100%;
`;

const Top2 = styled.div`
  width: 60px;
  height: 8px;
  border-radius: 3px;
  background-color: ${palette.webGray[5]};
`;

const PreviewHeader = styled.div`
  width: 100%;
  background: #fff;
  display: flex;
  align-items: center;
  border-bottom: 1px solid ${palette.webGray[15]};
  padding: 12px 15px;
  
  svg {
    font-size: 0.9rem;
    margin-right: 15px;
    color: #495057;
  }
  
  font-size: 0.95rem;
  font-weight: 600;
  color: #212529;
`;

const PreviewContent = styled.div`
  width: 100%;
  max-height: 450px;
  overflow-y: auto;
  background: ${palette.webGray[15]};
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${palette.webGray[4]};
    border-radius: 10px;
  }
  &::-webkit-scrollbar-track {
    background-color: ${palette.webGray[2]};
    border-radius: 10px;
  }
`;

const ContractDocument = styled.div`
  width: 100%;
  background: #fff;
  padding: 20px;
  min-height: 400px;
`;

const ContractTitle = styled.div`
  text-align: center;
  font-size: 1.1rem;
  font-weight: 700;
  color: #212529;
  margin-bottom: 25px;
  padding-bottom: 15px;
  border-bottom: 2px solid #212529;
`;

const ContractContent = styled.div`
  width: 100%;
  padding: 15px 0;
  word-break: keep-all;
  word-wrap: break-word;
  min-height: 200px;
  font-size: 0.85rem;
  line-height: 1.6;
  color: #212529;
  
  img {
    max-width: 100%;
    height: auto;
  }
  
  .ql-align-center {
    text-align: center;
  }
  
  .ql-align-right {
    text-align: right;
  }
  
  .ql-align-justify {
    text-align: justify;
  }
  
  .ql-size-small {
    font-size: 0.75em;
  }
  
  .ql-size-large {
    font-size: 1.2em;
  }
  
  .ql-size-huge {
    font-size: 1.5em;
  }
`;

const ContractSignature = styled.div`
  width: 100%;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;
`;

const SignatureDate = styled.div`
  text-align: left;
  font-size: 0.9rem;
  color: #212529;
  margin-bottom: 20px;
`;

const SignatureBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const SignatureRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  .label {
    font-size: 0.9rem;
    color: #212529;
    min-width: 80px;
  }
  
  .sign-box {
    flex: 1;
    text-align: right;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 10px;
  }
  
  .member-sign {
    padding: 8px 20px;
    border: 1px solid #ddd;
    border-radius: 4px;
    background: #f8f9fa;
    color: #999;
    font-size: 0.8rem;
  }
  
  .stamp {
    img {
      max-width: 60px;
      max-height: 60px;
      object-fit: contain;
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.5rem 2rem;
  border-top: 1px solid #e9ecef;
`;

const StampModeToggle = styled.div`
  display: inline-flex;
  border: 1px solid #dee2e6;
  border-radius: 999px;
  overflow: hidden;
  background: #f8f9fa;
`;

const ModeButton = styled.button`
  padding: 6px 18px;
  border: none;
  background: ${props => props.active ? '#fff' : 'transparent'};
  color: ${props => props.active ? palette.webCyan[6] : '#868e96'};
  font-weight: ${props => props.active ? 600 : 500};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #fff;
    color: ${palette.webCyan[6]};
  }

  &:disabled {
    cursor: not-allowed;
    color: #ced4da;
    background: transparent;
    font-weight: 500;
  }
`;

const SignatureHint = styled.div`
  margin-top: 8px;
  font-size: 0.8rem;
  color: #868e96;
`;

const SignatureCanvasWrapper = styled.div`
  border: 1px solid #dee2e6;
  border-radius: 6px;
  background: #fff;
  padding: 12px;
  max-width: 400px;
  margin: 0 auto;
`;

const SignatureCanvas = styled.canvas`
  width: 100%;
  aspect-ratio: 1 / 1;
  border: 1px solid #ced4da;
  border-radius: 4px;
  touch-action: none;
  cursor: crosshair;
  background: #fff;
  display: block;
`;

const CanvasActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
  gap: 8px;
`;

const CanvasButton = styled.button`
  padding: 6px 16px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  background: #f8f9fa;
  color: #495057;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:enabled {
    background: ${palette.webGray[1]};
  }

  &:disabled {
    cursor: not-allowed;
    color: #adb5bd;
  }
`;

const EContractModal = ({ visible, onClose, onConfirm, eContract, onUploadStamp, club }) => {
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [stampFile, setStampFile] = useState(null);
  const [stampPreview, setStampPreview] = useState('');
  const [existingStampURL, setExistingStampURL] = useState('');
  const [signatureMode, setSignatureMode] = useState('upload'); // 'upload' | 'draw'
  const [signaturePreview, setSignaturePreview] = useState('');
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignatureDrawn, setHasSignatureDrawn] = useState(false);

  const canvasRef = useRef(null);

  const initializeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.lineWidth = 3;
    ctx.strokeStyle = '#121212';
    setSignaturePreview('');
    setHasSignatureDrawn(false);
  }, []);

  useEffect(() => {
    if (eContract) {
      setTitle(eContract.title || '');
      setText(eContract.text || '');
      const stampURL = eContract.stempURL ? `https://kr.object.ncloudstorage.com/qnoss/clubdata/${eContract.stempURL}` : '';
      setExistingStampURL(stampURL);
      setStampFile(null);
      setStampPreview('');
      setSignatureMode('upload');
      setSignaturePreview('');
      setHasSignatureDrawn(false);
    } else {
      setTitle('');
      setText('');
      setStampFile(null);
      setStampPreview('');
      setExistingStampURL('');
      setSignatureMode('upload');
      setSignaturePreview('');
      setHasSignatureDrawn(false);
    }
  }, [eContract, visible]);

  useEffect(() => {
    if (signatureMode === 'draw' && visible) {
      requestAnimationFrame(() => {
        initializeCanvas();
      });
    }
  }, [signatureMode, visible, initializeCanvas]);

  const handleStampSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.includes('image/')) {
        alert('이미지 파일만 업로드 가능합니다.');
        return;
      }
      setSignatureMode('upload');
      setStampFile(file);
      setStampPreview(URL.createObjectURL(file));
      setExistingStampURL(''); // 새 파일 선택 시 기존 URL 제거
      setSignaturePreview('');
      setHasSignatureDrawn(false);
    }
  };

  const handleRemoveStamp = () => {
    setStampFile(null);
    setStampPreview('');
    setExistingStampURL('');
    setSignaturePreview('');
    setHasSignatureDrawn(false);
  };

  const handleSelectSignatureMode = (mode) => {
    if (
      mode === 'draw' &&
      signatureMode !== 'draw' &&
      (stampPreview || existingStampURL)
    ) {
      alert('기존 서명 이미지를 삭제한 후 직접 서명하기를 선택할 수 있습니다.');
      return;
    }
    setSignatureMode(mode);
    if (mode === 'upload') {
      setSignaturePreview('');
      setHasSignatureDrawn(false);
    } else {
      setStampFile(null);
      setStampPreview('');
      setExistingStampURL('');
      requestAnimationFrame(() => initializeCanvas());
    }
  };

  const getCanvasCoordinates = useCallback((event) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (event.clientX - rect.left) * scaleX,
      y: (event.clientY - rect.top) * scaleY,
    };
  }, []);

  const handlePointerDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (signatureMode !== 'draw') return;
    event.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const { x, y } = getCanvasCoordinates(event);
    ctx.beginPath();
    ctx.moveTo(x, y);
    if (canvas.setPointerCapture) {
      canvas.setPointerCapture(event.pointerId);
    }
    setIsDrawing(true);
  };

  const handlePointerMove = (event) => {
    if (!isDrawing || signatureMode !== 'draw') return;
    event.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const { x, y } = getCanvasCoordinates(event);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignatureDrawn(true);
  };

  const handlePointerUp = (event) => {
    if (!isDrawing || signatureMode !== 'draw') return;
    event.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.closePath();
    if (canvas.releasePointerCapture) {
      canvas.releasePointerCapture(event.pointerId);
    }
    setIsDrawing(false);
    const blank = isCanvasBlank();
    setHasSignatureDrawn(!blank);
    if (!blank) {
      const dataUrl = canvas.toDataURL('image/png');
      setSignaturePreview(dataUrl);
    } else {
      setSignaturePreview('');
    }
  };

  const handleClearCanvas = () => {
    initializeCanvas();
  };

  const isCanvasBlank = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return true;
    const ctx = canvas.getContext('2d');
    if (!ctx) return true;
    const pixelBuffer = new Uint32Array(
      ctx.getImageData(0, 0, canvas.width, canvas.height).data.buffer
    );
    return !pixelBuffer.some((color) => color !== 0xffffffff);
  }, []);

  const handleConfirm = async () => {
    if (!title.trim()) {
      alert('제목을 입력해주세요.');
      return;
    }
    if (!text.trim()) {
      alert('계약서 내용을 입력해주세요.');
      return;
    }

    let stempURL = '';

    if (signatureMode === 'upload') {
      if (stampFile) {
        const uploadedURL = await onUploadStamp(stampFile);
        if (uploadedURL) {
          stempURL = uploadedURL;
        }
      } else if (existingStampURL) {
        stempURL = eContract?.stempURL || '';
      }
    } else {
      if (!signaturePreview || isCanvasBlank()) {
        alert('서명을 그려주세요.');
        return;
      }
      const canvas = canvasRef.current;
      if (!canvas) {
        alert('서명 캔버스를 불러올 수 없습니다. 다시 시도해주세요.');
        return;
      }
      const blob = await new Promise((resolve) => {
        canvas.toBlob(resolve, 'image/png');
      });
      if (!blob) {
        alert('서명을 이미지로 변환하는 데 실패했습니다.');
        return;
      }
      const fileName = `signature_${Date.now()}.png`;
      const signatureFile = new File([blob], fileName, { type: 'image/png' });
      const uploadedURL = await onUploadStamp(signatureFile);
      if (uploadedURL) {
        stempURL = uploadedURL;
      }
    }

    onConfirm({
      eContractID: eContract?.clubEContractID || null,
      title: title.trim(),
      text: text.trim(),
      stempURL: stempURL || '',
    });
  };

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        ["bold", "italic", "underline", "strike"],
        [{ size: ["small", false, "large", "huge"] }],
        [
          { align: [] }, 
          { color: [] }
        ],
      ],
    },
    clipboard: { matchVisual: false }
  }), []);

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "size",
    "color",
    "bullet",
    "indent",
    "align"
  ];

  if (!visible) return null;

  console.log('stampPreview : ', stampPreview);
  console.log('existingStampURL : ', existingStampURL);

  const previewImage = signatureMode === 'draw' ? signaturePreview : (stampPreview || existingStampURL);

  return (
    <FullScreen onClick={()=>{}}>
      <ModalBlock onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <h2>{eContract ? '전자계약서 수정' : '전자계약서 추가'}</h2>
          <ButtonGroup style={{border: 'none', padding: 0}}>
            <Button onClick={onClose}>취소</Button>
            <Button onClick={handleConfirm} cyan>
              {eContract ? '수정' : '추가'}
            </Button>
          </ButtonGroup>
        </ModalHeader>

        <ModalContent>
          {/* 왼쪽: 입력 영역 */}
          <LeftSection>
            <FormGroup>
              <label>제목</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="뉴욕 피트니스 회원 이용 계약서"
                maxLength={50}
              />
            </FormGroup>

            <FormGroup>
              <label>계약서 내용</label>
              <QuillBox>
                <ReactQuill
                  value={text}
                  onChange={setText}
                  theme="snow"
                  modules={modules}
                  formats={formats}
                  placeholder="계약서 내용을 입력해주세요..."
                />
              </QuillBox>
            </FormGroup>

            <FormGroup>
              <label>서명 방식 선택</label>
              <StampModeToggle>
                <ModeButton
                  type="button"
                  active={signatureMode === 'upload'}
                  onClick={() => handleSelectSignatureMode('upload')}
                >
                  서명 이미지 업로드
                </ModeButton>
                <ModeButton
                  type="button"
                  active={signatureMode === 'draw'}
                  disabled={signatureMode !== 'draw' && (stampPreview || existingStampURL)}
                  onClick={() => handleSelectSignatureMode('draw')}
                >
                  직접 서명하기
                </ModeButton>
              </StampModeToggle>
            </FormGroup>

            {signatureMode === 'upload' ? (
              <FormGroup>
                <label>서명 이미지</label>
                <input
                  type="file"
                  id="stampInput"
                  accept="image/*"
                  onChange={handleStampSelect}
                  style={{ display: 'none' }}
                />
                <ImageUploadBox onClick={() => document.getElementById('stampInput').click()}>
                  {previewImage ? (
                    <>
                      <img src={previewImage} alt="서명" />
                      <div className="remove-btn" onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveStamp();
                      }}>
                        <FaTrash />
                      </div>
                    </>
                  ) : (
                    <div className="placeholder">
                      <ImPlus />
                      <span>서명 이미지 업로드</span>
                    </div>
                  )}
                </ImageUploadBox>
              </FormGroup>
            ) : (
              <FormGroup>
                <label>직접 서명하기</label>
                <SignatureCanvasWrapper>
                  <SignatureCanvas
                    ref={canvasRef}
                    width={400}
                    height={400}
                    onPointerDown={handlePointerDown}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                    onPointerLeave={handlePointerUp}
                  />
                  <CanvasActions>
                    <CanvasButton type="button" onClick={handleClearCanvas} disabled={!hasSignatureDrawn && !signaturePreview}>
                      지우기
                    </CanvasButton>
                  </CanvasActions>
                </SignatureCanvasWrapper>
                <SignatureHint>* 화면에 서명을 그리면 자동으로 이미지로 저장되어 계약서에 사용됩니다.</SignatureHint>
              </FormGroup>
            )}
          </LeftSection>

          {/* 오른쪽: 미리보기 */}
          <RightSection>
            <PreviewLabel>미리보기</PreviewLabel>
            <PreviewBox>
              <PreviewTop>
                <Top1 />
                <Top2 />
              </PreviewTop>
              <PreviewHeader>
                <FaArrowLeft />
                {title || '제목 없음'}
              </PreviewHeader>
              <PreviewContent>
                <ContractDocument>
                  <ContractTitle>
                    {title || '계약서 제목을 입력해주세요'}
                  </ContractTitle>
                  
                  <ContractContent dangerouslySetInnerHTML={{
                    __html: text || '<p style="color: #999; text-align: center;">계약서 내용을 입력해주세요.</p>'
                  }} />
                  
                  <ContractSignature>
                    <SignatureDate>
                      {new Date().getFullYear()}년 {String(new Date().getMonth() + 1).padStart(2, '0')}월 {String(new Date().getDate()).padStart(2, '0')}일
                    </SignatureDate>
                    
                    <SignatureBlock>
                      <SignatureRow>
                        <div className="label">회원 이름</div>
                        <div className="sign-box">
                          <div className="member-sign">(서명)</div>
                        </div>
                      </SignatureRow>
                      
                      <SignatureRow>
                        <div className="label">{club?.name || '클럽명'}</div>
                        <div className="sign-box">
                          {previewImage && (
                            <div className="stamp">
                              <img src={previewImage} alt="서명" />
                            </div>
                          )}
                        </div>
                      </SignatureRow>
                    </SignatureBlock>
                  </ContractSignature>
                </ContractDocument>
              </PreviewContent>
            </PreviewBox>
          </RightSection>
        </ModalContent>
      </ModalBlock>
    </FullScreen>
  );
};

export default EContractModal;
